const toastTypes = new Set(["success", "info", "warning", "danger"]);

const normalizeToastType = (type) => {
    const normalized = String(type || "info").toLowerCase();
    const compatibleType = normalized === "error" ? "danger" : normalized;
    return toastTypes.has(compatibleType) ? compatibleType : "info";
};

const normalizeToastPriority = (options) => (
    options.priority === "assertive" || options.ariaLive === "assertive"
        ? "assertive"
        : "polite"
);

const normalizeToastTimeout = (timeout) => (
    Number.isFinite(timeout) ? Math.max(0, timeout) : 4500
);

export const createToast = (options = {}) => ({
    id: window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
    type: normalizeToastType(options.type),
    title: options.title || "",
    message: options.message || "",
    timeout: normalizeToastTimeout(options.timeout),
    priority: normalizeToastPriority(options),
    remaining: normalizeToastTimeout(options.timeout),
    deadline: 0,
    paused: false,
    pauseReasons: []
});

export const setupRcdUiStore = (Alpine, bootUi, storeName = "rcdUi") => {
    if (Alpine.store(storeName)) {
        return Alpine.store(storeName);
    }

    const toastTimers = new Map();

    const clearToastTimer = (id) => {
        const timer = toastTimers.get(id);
        if (timer) {
            window.clearTimeout(timer.handle);
            toastTimers.delete(id);
        }
    };

    const scheduleToast = (store, toast) => {
        clearToastTimer(toast.id);

        if (toast.timeout <= 0 || toast.pauseReasons.length > 0) {
            return;
        }

        const duration = Math.max(0, toast.remaining);
        if (duration === 0) {
            store.dismissToast(toast.id);
            return;
        }

        toast.paused = false;
        toast.deadline = Date.now() + duration;
        const handle = window.setTimeout(() => {
            toastTimers.delete(toast.id);
            store.dismissToast(toast.id);
        }, duration);
        toastTimers.set(toast.id, { handle, deadline: toast.deadline });
    };

    Alpine.store(storeName, {
        blockCount: 0,
        blockMessage: "",
        toasts: [],
        confirmDialog: {
            open: false,
            title: "",
            message: "",
            confirmText: "Confirm",
            cancelText: "Cancel",
            variant: "primary",
            resolve: null
        },
        block(options = {}) {
            this.blockCount += 1;
            this.blockMessage = options.message || "";
        },
        unblock() {
            this.blockCount = Math.max(0, this.blockCount - 1);
            if (this.blockCount === 0) {
                this.blockMessage = "";
            }
        },
        toast(options = {}) {
            const toast = createToast(options);
            this.toasts.push(toast);

            if (typeof bootUi === "function") {
                window.requestAnimationFrame(bootUi);
            }

            scheduleToast(this, toast);
            return toast.id;
        },
        dismissToast(id) {
            clearToastTimer(id);
            this.toasts = this.toasts.filter((toast) => toast.id !== id);
        },
        pauseToast(id, reason = "interaction") {
            const toast = this.toasts.find((item) => item.id === id);
            if (!toast || toast.timeout <= 0) {
                return;
            }

            const pauseReason = String(reason || "interaction");
            if (!toast.pauseReasons.includes(pauseReason)) {
                toast.pauseReasons = [...toast.pauseReasons, pauseReason];
            }

            const timer = toastTimers.get(id);
            if (timer) {
                toast.remaining = Math.max(0, timer.deadline - Date.now());
                clearToastTimer(id);
            }

            toast.deadline = 0;
            toast.paused = true;
        },
        resumeToast(id, reason = "interaction") {
            const toast = this.toasts.find((item) => item.id === id);
            if (!toast || toast.timeout <= 0) {
                return;
            }

            const pauseReason = String(reason || "interaction");
            toast.pauseReasons = toast.pauseReasons.filter((item) => item !== pauseReason);
            toast.paused = toast.pauseReasons.length > 0;

            if (!toast.paused) {
                scheduleToast(this, toast);
            }
        },
        confirm(options = {}) {
            return new Promise((resolve) => {
                this.confirmDialog = {
                    open: true,
                    title: options.title || "",
                    message: options.message || "",
                    confirmText: options.confirmText || "Confirm",
                    cancelText: options.cancelText || "Cancel",
                    variant: options.variant || "primary",
                    resolve
                };
            });
        },
        resolveConfirm(accepted) {
            const resolve = this.confirmDialog.resolve;
            this.confirmDialog = {
                open: false,
                title: "",
                message: "",
                confirmText: "Confirm",
                cancelText: "Cancel",
                variant: "primary",
                resolve: null
            };

            if (resolve) {
                resolve(accepted);
            }
        }
    });

    return Alpine.store(storeName);
};

export const createRcdUiApi = (Alpine, storeName = "rcdUi") => ({
    blockUi(options) {
        Alpine.store(storeName).block(options);
        return () => Alpine.store(storeName).unblock();
    },
    unblockUi() {
        Alpine.store(storeName).unblock();
    },
    async withBlockUi(action, options) {
        const unblock = this.blockUi(options);
        try {
            return await action();
        } finally {
            unblock();
        }
    },
    toast(options) {
        return Alpine.store(storeName).toast(typeof options === "string" ? { message: options } : options);
    },
    confirm(options) {
        return Alpine.store(storeName).confirm(options);
    }
});

const normalizeSelectItem = (item) => {
    if (typeof item === "string") {
        return { value: item, text: item };
    }

    return {
        value: item?.value ?? item?.Value ?? item?.text ?? item?.Text ?? "",
        text: item?.text ?? item?.Text ?? item?.value ?? item?.Value ?? ""
    };
};

export const createSearchableSelect = (items = [], selectedValue = "") => ({
    open: false,
    query: "",
    selectedValue,
    sourceIsPrimitive: items.every((item) => typeof item === "string"),
    items: items.map(normalizeSelectItem).filter((item) => item.value && item.text),
    get selected() {
        return this.selectedText;
    },
    set selected(value) {
        this.selectedValue = value || "";
    },
    get selectedText() {
        return this.items.find((item) => item.value === this.selectedValue)?.text || "";
    },
    filtered() {
        const q = this.query.trim().toLowerCase();
        const results = q
            ? this.items.filter((item) =>
            item.text.toLowerCase().includes(q) ||
            item.value.toLowerCase().includes(q))
            : this.items;

        return this.sourceIsPrimitive
            ? results.map((item) => item.text)
            : results;
    },
    choose(item) {
        const normalized = normalizeSelectItem(item);
        this.selectedValue = normalized.value;
        this.open = false;
        this.query = "";
        this.$dispatch("rcd-select-change", { value: normalized.value, item: normalized });
    }
});

export const setupRcdSharedComponents = (Alpine) => {
    window.searchableSelect = createSearchableSelect;

    if (Alpine?.data) {
        Alpine.data("searchableSelect", createSearchableSelect);
    }
};
